import { FC } from 'react';
import { Button, Grid, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { PartySection } from '../partyFormStyles';

export interface FamilyMember {
  _id?: string;
  name: string;
  relation?: string;
  gender?: string;
  dateOfBirth?: string;
  age?: number | '';
  phone?: string;
  /** Member's own party id (buyer/supplier members); invoices for the member bill this party. */
  counterParty?: string;
}

export const RELATIONS = ['Spouse', 'Father', 'Mother', 'Son', 'Daughter', 'Brother', 'Sister', 'Grandfather', 'Grandmother', 'Grandson', 'Granddaughter', 'Guardian', 'Other'];

// "Brother of Kunal Durudkar" for a party created from a family member; '' otherwise.
export const memberOfLabel = (party: any): string =>
  party?.parentParty?.name ? `${party.parentRelation || 'Member'} of ${party.parentParty.name}` : '';

// Completed years from a YYYY-MM-DD string; '' when it isn't a usable past date.
const ageFrom = (value: string): number | '' => {
  if (!value) return '';
  const dob = new Date(value);
  if (isNaN(dob.getTime())) return '';
  const today = new Date();
  let years = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) years--;
  return years >= 0 ? years : '';
};

// Stored members come back with ISO dates; the date input wants YYYY-MM-DD.
export const emptyFamilyMember = (): FamilyMember => ({ name: '', relation: '', gender: '', dateOfBirth: '', age: '', phone: '' });

// Saved members, or one blank row so the section is ready to fill.
export const toFamilyMembers = (list: any[] = []): FamilyMember[] =>
  !list || !list.length ? [emptyFamilyMember()] : list.map((m) => ({
    _id: m._id,
    name: m.name || '',
    relation: m.relation || '',
    gender: m.gender || '',
    dateOfBirth: m.dateOfBirth ? String(m.dateOfBirth).slice(0, 10) : '',
    age: m.age ?? '',
    phone: m.phone || '',
    counterParty: m.counterParty || undefined,
  }));

interface Props {
  value: FamilyMember[];
  onChange: (members: FamilyMember[]) => void;
  readOnly?: boolean;
  /** Section heading; null when the host already shows one. */
  heading?: string | null;
}

// Family members of a buyer/supplier: Name, Relation, Gender, DOB, Age (DOB fills Age), Phone.
const FamilyMembers: FC<Props> = ({ value, onChange, readOnly, heading = 'Family Details' }) => {
  const update = (i: number, patch: Partial<FamilyMember>) =>
    onChange(value.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const add = () => onChange([...value, emptyFamilyMember()]);
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));

  const input = { disableUnderline: true, readOnly };
  // View-only: hide the blank starter row.
  const rows = readOnly ? value.filter((m) => m.name.trim()) : value;

  return (
    <>
      {heading && <PartySection heading={heading} />}
      {rows.length === 0 && readOnly &&
        <Grid item xs={12} className="full-row"><Typography variant="body2" sx={{ color: '#9ca3af' }}>No family members</Typography></Grid>}
      {rows.map((m, i) => (
        <Grid item xs={12} className="full-row" key={m._id || i}>
          <Grid container columnSpacing={2} rowSpacing={1} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={2.5}>
              <h5 className='c-label'>Name</h5>
              <TextField className='c-text-field' variant="standard" size='small' sx={{ width: '100%' }} placeholder='Name'
                value={m.name} onChange={(e) => update(i, { name: e.target.value })} InputProps={input} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <h5 className='c-label'>Relation</h5>
              <TextField select className='c-text-field' variant="standard" size='small' sx={{ width: '100%' }}
                value={m.relation || ''} onChange={(e) => update(i, { relation: e.target.value })} InputProps={input}>
                <MenuItem value=''>Select Relation</MenuItem>
                {RELATIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={1.5}>
              <h5 className='c-label'>Gender</h5>
              <TextField select className='c-text-field' variant="standard" size='small' sx={{ width: '100%' }}
                value={m.gender || ''} onChange={(e) => update(i, { gender: e.target.value })} InputProps={input}>
                <MenuItem value=''>Select Gender</MenuItem>
                <MenuItem value='Male'>Male</MenuItem>
                <MenuItem value='Female'>Female</MenuItem>
                <MenuItem value='Other'>Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <h5 className='c-label'>Date of Birth</h5>
              <TextField type='date' className='c-text-field' variant="standard" size='small' sx={{ width: '100%' }}
                value={m.dateOfBirth || ''}
                onChange={(e) => { const d = e.target.value; const a = ageFrom(d); update(i, a === '' ? { dateOfBirth: d } : { dateOfBirth: d, age: a }); }}
                InputProps={input} />
            </Grid>
            <Grid item xs={12} sm={4} md={1}>
              <h5 className='c-label'>Age</h5>
              <TextField type='number' className='c-text-field' variant="standard" size='small' sx={{ width: '100%' }} placeholder='Age'
                value={m.age ?? ''} onChange={(e) => update(i, { age: e.target.value === '' ? '' : Number(e.target.value) })} InputProps={input} />
            </Grid>
            <Grid item xs={9} sm={7} md={2.5}>
              <h5 className='c-label'>Phone</h5>
              <TextField type='tel' className='c-text-field' variant="standard" size='small' sx={{ width: '100%' }} placeholder='Phone'
                value={m.phone || ''} onChange={(e) => update(i, { phone: e.target.value })} InputProps={input} />
            </Grid>
            {!readOnly &&
              <Grid item xs={3} sm={1} md={0.5} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => remove(i)} sx={{ color: '#dc2626', mb: 0.25 }}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Grid>}
          </Grid>
        </Grid>
      ))}
      {!readOnly &&
        <Grid item xs={12} className="full-row">
          <Button size="small" startIcon={<AddIcon />} onClick={add} sx={{ textTransform: 'none' }}>Add Member</Button>
        </Grid>}
    </>
  );
};

export default FamilyMembers;
